import { ref } from 'vue'
import type { DnsLookupResult, ViteAppConfiguration } from '../types'

/**
 * Composable for DNS lookup functionality
 * Handles API calls to WordPress REST API for DNS record fetching
 */
export function useDnsLookup() {
  const isLoading = ref(false)
  const results = ref<DnsLookupResult | null>(null)
  const error = ref<string | null>(null)

  /**
   * Look up DNS records for a domain
   * 
   * @param domain - Domain name to look up
   * @param recordTypes - Array of DNS record types to fetch (e.g., ['A', 'MX', 'TXT'])
   * @param dnsServer - Optional DNS server to query (e.g., '8.8.8.8')
   */
  const lookup = async (domain: string, recordTypes: string[], dnsServer?: string) => {
    isLoading.value = true
    error.value = null
    results.value = null

    try {
      // Get WordPress configuration from global
      const config = (window as any).conflatedDomainsConfig as ViteAppConfiguration

      if (!config || !config.restUrl || !config.nonce) {
        throw new Error('WordPress REST API configuration not found')
      }

      const requestBody: any = {
        domain: domain,
        record_types: recordTypes
      }

      if (dnsServer) {
        requestBody.dns_server = dnsServer
      }

      const response = await fetch(`${config.restUrl}/${config.apiNamespace}/tools/dns-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        throw new Error(`API request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      // WordPress REST API wraps response in {success: true, data: {...}}
      const responseData = data.data || data

      if (!responseData.success) {
        throw new Error(responseData.error || 'DNS lookup failed')
      }

      results.value = responseData

    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unknown error occurred'
      console.error('DNS lookup error:', e)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear lookup results and errors
   */
  const clear = () => {
    results.value = null
    error.value = null
    isLoading.value = false
  }

  return {
    isLoading,
    results,
    error,
    lookup,
    clear
  }
}
